import React from 'react';
import PropTypes from 'prop-types';
import CSSModules from 'react-css-modules';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';
import { autobind } from 'core-decorators';
import numberFormatter from 'utils/numberFormatter';
import * as FormatUtils from 'utils/formatUtils';
import { Field } from 'redux-form';
import Input from 'components/common/material-form/Input';
import RadioGroup from 'components/common/material-form/RadioGroup';
import RadioButton from 'components/common/material/RadioButton';
import Dropdown from 'components/common/material/DropDown';
import Dialog from 'components/common/material/Dialog';
import * as purchaseConstants from '../purchases/constants';
import { getPurchaseMergedStatus } from '../purchases/helpers';
import * as constants from './constants';
import payNowStyles from './payNow.scss';
import styles from './paymentAmount.scss';

@injectIntl
@CSSModules({ ...styles, ...payNowStyles }, { allowMultiple: true })
class PaymentAmount extends React.Component {

  static propTypes = {
    intl: intlShape,
    purchase: PropTypes.shape({ //props
      leftAmountToPay: PropTypes.number.isRequired,
      externalId: PropTypes.string.isRequired,
      cardLast4Digits: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      allPaymentsIncludingDownPayment: PropTypes.array.isRequired,
      isZeroAprPromoted: PropTypes.bool,
      zeroAprPromotionExpirationDate: PropTypes.string,
    }).isRequired,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    nextPaymentsToPay: PropTypes.number.isRequired,
    applyAmountToNextPayments: PropTypes.bool.isRequired,
    amountOption: PropTypes.string.isRequired,
    onAmountChange: PropTypes.func.isRequired,
    changeAmountAction: PropTypes.func.isRequired,
    changeNextPaymentsToPayAction: PropTypes.func.isRequired,
    changeAmountOptionAction: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.setDefaultAmountOption(props);
    this.state = {
      isNextPaymentsMenuOpen: false,
      isZeroAprPromotionalDialogOpen: false
    };
  }

  setDefaultAmountOption(props) {
    if (props && props.purchase && props.purchase.pastDueAmount) {
      props.changeAmountOptionAction(constants.PAY_NOW_AMOUNT_OPTION_PAST_DUE);
    }
  }

  @autobind
  toggleNextPaymentsMenu(e) {
    if (e) {
      e.preventDefault();
    }
    this.setState({
      isNextPaymentsMenuOpen: !this.state.isNextPaymentsMenuOpen/* && this.props.amountOption === constants.PAY_NOW_AMOUNT_OPTION_NEXT*/
    });
  }

  @autobind
  toggleZeroAprPromotionalDialogOpen() {
    this.setState({ isZeroAprPromotionalDialogOpen: !this.state.isZeroAprPromotionalDialogOpen });
  }

  @autobind
  closeNextPaymentsMenu() {
    this.setState({
      isNextPaymentsMenuOpen: false
    });
  }

  @autobind
  toggleAmountOptionNext() {
    this.props.changeAmountOptionAction(constants.PAY_NOW_AMOUNT_OPTION_NEXT);
  }

  @autobind
  handleSelectNextPaymentsToPay(selectedNextPayments) {
    const { purchase: { leftAmountToPay } } = this.props;
    const payablePayments = this.getPayablePayments();
    let amount = 0;
    let nextPaymentsToPayDates = [];
    payablePayments.forEach((payment, index) => {
      if (index < selectedNextPayments) {
        amount += payment.amount;
        nextPaymentsToPayDates.push(payment.dueDate);
      }
    }, 0);
    if (selectedNextPayments === payablePayments.length) {
      amount = leftAmountToPay;
      nextPaymentsToPayDates = [];
    }
    this.props.changeNextPaymentsToPayAction(selectedNextPayments, nextPaymentsToPayDates);
    this.props.changeAmountAction(numberFormatter.roundTwoDecimalPoints(amount));
    this.closeNextPaymentsMenu();
  }

  normalizeAmountField(value) {
    return (numberFormatter.forceNotNegativeAndTwoDecimalPoints(value) || '') + '';
  }

  getPayablePayments() {
    const { purchase: { allPaymentsIncludingDownPayment } } = this.props;
    return allPaymentsIncludingDownPayment.filter((payment) => {
      return ['next', 'pending'].indexOf(payment.status.toLowerCase()) >= 0;
    });
  }

  renderPayTotalBalance() {
    const { amount } = this.props;
    return (
      <div styleName="remainingBalanceWrap">
        <div styleName="payTotalBalance">{FormatUtils.addPriceSymbol(amount)}</div>
        <div styleName="partition amountOptionInfo" data-at="paymentDescription">
          {this.renderAmountOptionInfoText()}
        </div>
      </div>
    );
  }

  renderPayNextPaymentsDropDown() {
    const { nextPaymentsToPay } = this.props;
    const payments = this.getPayablePayments();

    const theme = {
      values: styles.nextPaymentsDropDownValues
    };

    return (
      <div className={styles.nextPayments}>
        <Dropdown
          theme={theme}
          onFocus={this.toggleAmountOptionNext}
          value={nextPaymentsToPay}
          onChange={this.handleSelectNextPaymentsToPay}
          source={payments.map((payment, index) => ({ value: index + 1, label: index + 1 }))}
        />
      </div>
    );
  }

  renderZeroAprPromotionText() {
    const values = {
      promotionalPeriod: <a onClick={this.toggleZeroAprPromotionalDialogOpen}><FormattedMessage id="payNow.amount.entire.promotionalPeriod" /></a>
    };
    return (
      <div styleName="promotionalText">
        <FormattedMessage id="payNow.amount.entire.promotion" values={values} />
      </div>
    );
  }

  renderZeroAprPromotionDialog() {
    const { purchase: { zeroAprPromotionExpirationDate }, amount, intl: { formatMessage } } = this.props;

    const values = {
      amount: FormatUtils.addPriceSymbol(amount),
      date: FormatUtils.transformDate(zeroAprPromotionExpirationDate)
    };

    const actions = [
      { onClick: this.toggleZeroAprPromotionalDialogOpen, primary: true, label: formatMessage({ id: 'common.ok' }) },
    ];

    return (
      <Dialog active={true} actions={actions}>
        <FormattedMessage id="payNow.amount.entire.promotional.dialog" values={values} />
      </Dialog>
    );
  }

  getPastDueMessage() {
    const { applyAmountToNextPayments } = this.props;
    const isAmountExeedingPastdue = !this.props.purchase.pastDueAmount || this.props.amount > this.props.purchase.pastDueAmount

    if (isAmountExeedingPastdue) {
      return applyAmountToNextPayments ? 'payNowPastDue.amount.option.partial.info.nextPayments' : 'payNowPastDue.amount.option.partial.info.lastPayments';
    }
    return "payNowPastDue.amount.option.partial.info.noExcessAmount"
  }

  renderAmountOptionInfoText() {
    const { amountOption, applyAmountToNextPayments } = this.props;
    const isLate = getPurchaseMergedStatus(this.props.purchase) === purchaseConstants.PURCHASE_STATUS_MERGED_LATE;

    let message = applyAmountToNextPayments ? 'payNow.amount.option.partial.info.nextPayments' : 'payNow.amount.option.partial.info.lastPayments'
    message = isLate ? this.getPastDueMessage() : message;
    switch (amountOption) {
      case constants.PAY_NOW_AMOUNT_OPTION_PARTIAL:
        return (
          <FormattedMessage id={message} />
        );
      case constants.PAY_NOW_AMOUNT_OPTION_NEXT:
        return (
          <FormattedMessage id="payNow.amount.option.next.info" />
        );
      case constants.PAY_NOW_AMOUNT_OPTION_ENTIRE:
        return (
          <FormattedMessage id="payNow.amount.option.entire.info" />
        );
      case constants.PAY_NOW_AMOUNT_OPTION_PAST_DUE:
        return (
          <FormattedMessage id="payNowPastDue.amount.option.totalPastDue" />
        );
    }
  }

  isApplyAmountToNextOrLastPaymentVisible() {
    if (this.props.amountOption === constants.PAY_NOW_AMOUNT_OPTION_PARTIAL) {
      return !this.props.purchase.pastDueAmount || this.props.amount > this.props.purchase.pastDueAmount
    }
    return false;
  }

  render() {
    const { amountOption, amount, purchase: { payments, leftAmountToPay, isZeroAprPromoted, pastDueAmount }, intl: { formatMessage } } = this.props;
    const nextPayment = payments.find((payment) => payment.status === 'next') || {};

    return (
      <div styleName="paymentAmount">
        <h2 styleName="heading" data-at="pay-now-amount-title">{formatMessage({ id: 'payNow.amount.title' })}</h2>
        <div styleName="inner">
          <Field component={RadioGroup} name="amountOption" className={styles.amountOptions}>
            {pastDueAmount > 0 && <RadioButton dataAt="entireLateSlct" wrapClassName={payNowStyles.optionWrap} styleName="option" value={constants.PAY_NOW_AMOUNT_OPTION_PAST_DUE} label={formatMessage({ id: 'payNow.amount.option.pastDue' }, { pastDueAmount: FormatUtils.addPriceSymbol(pastDueAmount) })} />}
            <RadioButton dataAt="entireOutstandingSlct" wrapClassName={payNowStyles.optionWrap} styleName="option" value={constants.PAY_NOW_AMOUNT_OPTION_ENTIRE} label={formatMessage({ id: 'payNow.amount.option.entire' }, { leftAmountToPay: FormatUtils.addPriceSymbol(leftAmountToPay) })} />
            <RadioButton dataAt="oneTimePaymentSlct" wrapClassName={payNowStyles.optionWrap} styleName="option" value={constants.PAY_NOW_AMOUNT_OPTION_PARTIAL} label={formatMessage({ id: 'payNow.amount.option.partial' })} />
          </Field>
          <div styleName="amountOuterWrap">
            {
              amountOption === constants.PAY_NOW_AMOUNT_OPTION_PARTIAL &&
              <div>
                <div styleName="amountWrap">
                  <Field data-at="otherAmountInput" name="amount" theme={styles} component={Input} normalize={this.normalizeAmountField} hint={formatMessage({ id: 'payNow.amount.partial.hint' })} />
                </div>
                <div styleName="partition amountOptionInfo" data-at="paymentDescription">
                  {this.renderAmountOptionInfoText()}
                </div>
              </div>
            }
            {amountOption === constants.PAY_NOW_AMOUNT_OPTION_NEXT && this.renderPayTotalBalance()}
            {amountOption === constants.PAY_NOW_AMOUNT_OPTION_PAST_DUE && this.renderPayTotalBalance()}
            {amountOption === constants.PAY_NOW_AMOUNT_OPTION_ENTIRE && this.renderPayTotalBalance()}
            {isZeroAprPromoted && Number(amount) === leftAmountToPay && this.renderZeroAprPromotionText()}
            {this.state.isZeroAprPromotionalDialogOpen && this.renderZeroAprPromotionDialog()}
          </div>
          <div styleName="amountPartialAmountApplyType">
            {
              this.isApplyAmountToNextOrLastPaymentVisible() &&
              <div>
                <div styleName="amountPartialAmountApplyTypeTitle">{formatMessage({ id: 'payNow.amount.partial.appyOption.title' })}</div>
                <div styleName="applyAmountToNextPaymentsWrap">
                  <Field component={RadioGroup} name="applyAmountToNextPayments" >
                    <RadioButton dataAt="nextPaymentSlct" wrapClassName={payNowStyles.optionWrap} styleName="option" value={true} label={formatMessage({ id: 'payNow.amount.partial.appyOption.next' })} />
                    <RadioButton dataAt="lastPaymentSlct" wrapClassName={payNowStyles.optionWrap} styleName="option" value={false} label={formatMessage({ id: 'payNow.amount.partial.appyOption.last' })} />
                  </Field>
                  <div styleName="nextPaymentDueText" data-at="nextPaymentDueText">{formatMessage({ id: 'payNow.amount.partial.nextPaymentDue' }, { amount: FormatUtils.addPriceSymbol(nextPayment.amount), dueDate: FormatUtils.transformDate(nextPayment.dueDate) })}</div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    );
  }
}


export default PaymentAmount;



// WEBPACK FOOTER //
// ./src/pay-now/PaymentAmount.js